import { History, ArrowLeft, MapPin, Building2, FileText, Banknote, Users, Container, Droplets, Truck, UserCog,
  ReceiptText, ClipboardCheck, Wallet, CalendarCheck, TrendingDown, Clock, Trophy, MessageSquare,
  AlertTriangle, ShieldAlert, Activity, Satellite, BarChart3, FileBadge, ScanLine, Plug, KeyRound, Briefcase, FolderInput, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ModuleGroup, type ModuleDef } from '@/components/dashboard/ModuleGroup';

// PR-A1 — one Settings area holding ALL configuration, setup, admin and
// secondary tools. Nothing is deleted; every route here still works — it is
// just moved off the daily working dashboard.

const SETUP_MASTERS: ModuleDef[] = [
  { key: 'sites',           icon: MapPin,     color: 'text-cyan-500',    route: '/dashboard/sites' },
  { key: 'clients',         icon: Building2,  color: 'text-violet-500',  route: '/dashboard/clients' },
  { key: 'contracts',       icon: FileText,   color: 'text-blue-500',    route: '/dashboard/contracts' },
  { key: 'trip_rate_cards', icon: Banknote,   color: 'text-emerald-500', route: '/dashboard/settings/trip-rates' },
  { key: 'dumper_clients',  icon: Building2,  color: 'text-amber-500',   route: '/dashboard/dumpers/clients' },
  { key: 'investors',       icon: Users,      color: 'text-blue-500',    route: '/dashboard/investors' },
  { key: 'bodies',          icon: Container,  color: 'text-stone-500',   route: '/dashboard/vehicles/bodies' },
  { key: 'water_suppliers', icon: Droplets,   color: 'text-sky-500',     route: '/dashboard/water-suppliers' },
  { key: 'water_stations',  icon: Droplets,   color: 'text-sky-500',     route: '/dashboard/water-stations' },
  { key: 'vehicle_import',  icon: Truck,      color: 'text-green-500',   route: '/dashboard/imports/vehicles' },
  { key: 'driver_import',   icon: UserCog,    color: 'text-purple-500',  route: '/dashboard/imports/drivers' },
  // Next to the imports on purpose: filing what an import brought in is
  // the same job, finished.
  { key: 'sides',           icon: FolderInput, color: 'text-gold',       route: '/dashboard/settings/sides' },
  { key: 'change_requests', icon: Inbox,      color: 'text-rose-500',   route: '/dashboard/settings/change-requests' },
];

const MONEY_PAYROLL: ModuleDef[] = [
  { key: 'expenses',        icon: ReceiptText,  color: 'text-rose-500',    route: '/dashboard/expenses' },
  { key: 'closings',        icon: ClipboardCheck, color: 'text-indigo-500', route: '/dashboard/closings/admin' },
  { key: 'driver_advances', icon: Wallet,       color: 'text-indigo-500',  route: '/dashboard/cashflow/advances' },
  { key: 'salary_runs',     icon: CalendarCheck, color: 'text-amber-500',  route: '/dashboard/flatbed/salary-runs' },
  { key: 'driver_expenses', icon: UserCog,      color: 'text-purple-500',  route: '/dashboard/driver-expenses' },
];

const OVERSIGHT: ModuleDef[] = [
  { key: 'consumption',     icon: TrendingDown,  color: 'text-amber-500', route: '/dashboard/fuel/consumption' },
  { key: 'idle_waste',      icon: Clock,         color: 'text-amber-500', route: '/dashboard/fuel/idle-waste' },
  { key: 'driver_perf',     icon: Trophy,        color: 'text-amber-500', route: '/dashboard/fuel/driver-leaderboard' },
  { key: 'coaching',        icon: MessageSquare, color: 'text-amber-500', route: '/dashboard/fuel/coaching' },
  { key: 'monthly_reports', icon: FileText,      color: 'text-amber-500', route: '/dashboard/fuel/monthly-reports' },
  { key: 'anomalies',       icon: AlertTriangle, color: 'text-rose-500',  route: '/dashboard/anomalies' },
  { key: 'data_quality',    icon: ShieldAlert,   color: 'text-amber-500', route: '/dashboard/settings/data-quality' },
  { key: 'tracker_health',  icon: Satellite,     color: 'text-emerald-500', route: '/dashboard/settings/tracker-health' },
  { key: 'operations_live', icon: Activity,      color: 'text-rose-500',  route: '/dashboard/operations-live' },
  { key: 'fleet_live',      icon: Satellite,     color: 'text-sky-500',   route: '/dashboard/fleet/live' },
  { key: 'vehicle_heatmap', icon: BarChart3,     color: 'text-blue-500',  route: '/dashboard/analytics/vehicles' },
  { key: 'project_reports', icon: BarChart3,     color: 'text-blue-500',  route: '/dashboard/reports/project' },
  { key: 'reports',         icon: FileText,      color: 'text-blue-500',  route: '/dashboard/reports' },
  { key: 'document_packs',  icon: FileBadge,     color: 'text-emerald-500', route: '/dashboard/documents/packs' },
  { key: 'document_expiry', icon: FileBadge,     color: 'text-amber-500', route: '/dashboard/documents/expiry' },
  { key: 'auto_detected_trips', icon: Activity,  color: 'text-violet-500', route: '/dashboard/trips/auto-detected' },
];

const CONFIG: ModuleDef[] = [
  { key: 'trip_detection',  icon: Activity, color: 'text-violet-500', route: '/dashboard/settings/trip-detection' },
  { key: 'ocr_settings',    icon: ScanLine, color: 'text-amber-500',  route: '/dashboard/ocr-settings' },
  { key: 'afaqy_accounts',  icon: Satellite, color: 'text-sky-500',   route: '/dashboard/settings/afaqy-accounts' },
  { key: 'petroapp',        icon: Plug,     color: 'text-emerald-500', route: '/dashboard/petroapp' },
  { key: 'fleetplus',       icon: Plug,     color: 'text-orange-500', route: '/dashboard/settings/fleetplus' },
  { key: 'zoho',            icon: Plug,     color: 'text-blue-500',   route: '/dashboard/settings/zoho' },
];

const ACCESS: ModuleDef[] = [
  { key: 'users',             icon: KeyRound,   color: 'text-rose-500',  route: '/dashboard/settings/users' },
  { key: 'permissions',       icon: ShieldAlert, color: 'text-blue-500', route: '/dashboard/settings/permissions' },
  { key: 'report_recipients', icon: Users,      color: 'text-violet-500', route: '/dashboard/settings/report-recipients' },
  { key: 'company_documents', icon: FileBadge,  color: 'text-blue-600',  route: '/dashboard/settings/company-documents' },
  { key: 'audit',             icon: ShieldAlert, color: 'text-stone-500', route: '/dashboard/settings/audit' },
  { key: 'activity',          icon: FileBadge,  color: 'text-violet-500', route: '/dashboard/activity' },
];

const OTHER: ModuleDef[] = [
  { key: 'projects',     icon: Briefcase,  color: 'text-blue-600',   route: '/dashboard/projects' },
  { key: 'construction', icon: Building2,  color: 'text-orange-600', route: '/dashboard/construction' },
  { key: 'leaderboard',  icon: Trophy,     color: 'text-amber-500',  route: '/dashboard/leaderboard' },
];

/** Short names, because "NEOM Water Operations" does not fit a sentence. */
export default function SettingsHubPage() {
  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} variant="mark" />
            <div className="font-display text-lg">Settings &amp; configuration</div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* The side note and the intro line were removed. The side itself is
            still in force - it travels as X-Project-Id with every request - so
            these options still open scoped to it; the page simply no longer
            says so. */}
        <ModuleGroup groupLabel="Setup &amp; masters" modules={SETUP_MASTERS} />
        <ModuleGroup groupLabel="Money &amp; payroll" modules={MONEY_PAYROLL} />
        <ModuleGroup groupLabel="Fleet oversight &amp; analytics" modules={OVERSIGHT} />
        <ModuleGroup groupLabel="Config &amp; integrations" modules={CONFIG} />
        <ModuleGroup groupLabel="People &amp; access" modules={ACCESS} />
        <ModuleGroup groupLabel="Other" modules={OTHER} />
      </main>
    </div>
  );
}
